import _isEmpty from 'underscore/cjs/isEmpty.js'

import { MAX_COMPARE_POLICIES } from '~/constants/healthinsurance.constants'
import { useUtmsStore } from './utms'

export const useHealthInsuranceStore = defineStore('health-insurance', {
  state: () => ({
    page: null,

    guides: {
      url: null,
      initialPage: ''
    },
    form: {},

    hospitalCovers: {},
    extraCovers: {},
    policyId: 0,
    premiumType: '',
    state: '',
    reference: '',
    tier: '',
    directDebit: '',
    loading: 0,
    ageBasedDiscount: 0,
    percentage: '',

    refineParams: {
      searchBy: '',
      responseType: 'min',
      'search[lifestage]': '',
      'search[state]': '',
      'search[income]': 'nil',
      'search[primary_age]': 1,
      'search[age_secondary]': 1,
      'search[age_primary]': 1,
      'search[hospital_benefit_level]': '',
      'search[extra_benefit_level]': '',
      'search[excess]': 750,
      'search[rebate_percentage]': 0.24608, // ??
      'search[premium_type]': 'fortnightly',
      'search[hospital_covers]': '',
      'search[extra_covers]': '',
      'search[co_payment]': 'all',
      'search[age_based_discount]': 1,
      filterCurrentFund: true,
      sort: 'price'
    },

    quotesData: {},
    activeRebates: [],

    // HI-1601 - the classic results panel's cover selection, mirrored here from
    // its component-local `defaultFilters` because `refineParams` only carries
    // covers after a refine. `refineParams` wins when both are populated.
    leadSelectedCovers: {
      hospital: [],
      extras: []
    },

    // Policies selected for side-by-side comparison on the QRP (HI-1384).
    // Lives here rather than in the QRP template so the selection survives
    // navigation to the dedicated /quotes-results/compare view — the whole
    // store persists to sessionStorage, same as quotesData.
    // comparePolicyIds is the v-model target for the "Add to compare" checkbox;
    // comparePolicies holds the full policy objects the comparison renders from.
    comparePolicyIds: [],
    comparePolicies: [],

    // Envelope-level quote facts the comparison view needs but which are not on
    // the individual policy objects — the Govt rebate is per-response, not
    // per-quote. Captured on the QRP so the dedicated view doesn't refetch quotes.
    compareDetails: {},

    // Filters captured from the standalone QRP-landing page (no lead/uuid flow).
    // Distinctly named so applicationV2 can seed applicationData from these.
    qrpLandingFilters: {
      state: '',
      lifestage: '',
      income: '',
      agePrimary: 0,
      ageSecondary: 0,
      excess: 750,
      premiumType: 'fortnightly',
      hospitalCovers: [],
      extraCovers: [],
      insurer: ''
    }
  }),

  getters: {
    leadParams () {
      const form = this.form

      if (_isEmpty(form)) {
        return {}
      }

      const utmsStore = useUtmsStore()
      const formData = flattenObj(form, 1)

      const covers = (() => {
        if (!formData?.cover_type) {
          return {}
        }

        return getKey(formData.cover_type.toLowerCase().replace(/ /, '_'), {
          hospital_only: {
            hospital_level: 'silver plus',
            extra_level: 'nil'
          },
          extras_only: {
            hospital_level: 'nil',
            extra_level: 'low'
          },
          default: {
            hospital_level: 'silver plus',
            extra_level: 'low'
          }
        })
      })()

      let tempPostcode = {}
      if (typeof formData?.postcode === 'string') {
        tempPostcode = {
          postcode: formData?.postcode,
          state: formData?.state,
          suburb: formData?.suburb
        }
      } else if (typeof formData?.postcode === 'object') {
        tempPostcode = {
          ...formData?.postcode
        }
      }

      let tempHealthCover
      if (typeof formData?.health_cover === 'string') {
        tempHealthCover = formData?.health_cover
      } else {
        tempHealthCover = joinArr(formData?.health_cover)
      }

      const obj = {
        ...covers,
        ...getName(formData?.full_name),
        age: formData?.age,
        age_secondary: formData?.age_secondary,
        birthdate: formData?.birthdate,
        partner_birthdate: formData?.partner_birthdate,
        cover_type: formData?.cover_type,
        email: formData?.email,
        extras_cover_list: joinArr(formData?.extras_cover_list),
        health_cover: tempHealthCover,
        health_fund: (formData?.health_fund?.value || formData?.health_fund) ?? 'not insured',
        hospital_cover_list: joinArr(formData?.hospital_cover_list),
        income: formData?.income,
        life_stage: formData?.life_stage,
        partner_health_fund: ['Couple', 'Family'].includes(formData.life_stage)
          ? (formData.partner_health_fund?.value || formData.partner_health_fund) ?? 'not insured'
          : '',
        phone: formData?.phone,
        ...tempPostcode,

        brand: 'CC',
        enquiry_source: 'cc_nbf',
        form_page: import.meta.client && window.location.href,
        landing_page: utmsStore.GET_UTMS_LANDING_PAGE

      }

      return obj
    }
  },

  actions: {
    SET_STATE (data) {
      if (this.$state === undefined || data.value === undefined) {
        // eslint-disable-next-line no-console
        console.error('SET_STATE params[\'state\', \'value\'] are required')
      }

      this.$state[data.state] = data.value
    },

    getAvailableTime ({ state, vertical, limit, category }) {
      return useFetch('/api/callback/availability', {
        params: {
          state,
          vertical,
          limit,
          category
        }
      }).then(response => {
        const res = response?.data.value ?? {}

        if (!_isEmpty(res?.error)) {
          console.error('Error fetching callback timings:', res?.error?.statusMessage || 'Unknown error') // eslint-disable-line no-console
          return {}
        }

        return res?.data ?? {}
      }).catch(error => {
        throw error
      })
    },

    async createLead (payload) {
      const utmsStore = useUtmsStore()

      const response = await useFetch('/api/lead/create', {
        method: 'post',
        body: {
          vertical: 'Health Insurance',
          ...utmsStore.utms,
          ...payload
        }
      }).then(res => {
        const temp = res?.data.value

        if (temp?.error) {
          throw temp?.error
        }

        const data = temp?.data

        return data?.lead_uuid ?? undefined
      }).catch(error => {
        throw error
      })

      return response
    },

    optInCall (payload) {
      return useFetch('/api/health-insurance/optin', {
        method: 'post',
        body: {
          ...payload
        }
      }).then(response => {
        const res = response?.data.value ?? {}

        if (!_isEmpty(res?.error)) {
          return {}
        }

        return res?.data ?? {}
      }).catch(error => {
        throw error
      })
    },

    updateLeadRefineData (uuid) {
      try {
        $fetch('/api/health-insurance/customer/update-lead-refine-params', {
          method: 'post',
          body: {
            uuid,
            refineParams: {
              'hospital_covers': this.refineParams['search[hospital_covers]'],
              'extra_covers': this.refineParams['search[extra_covers]']
            }
          }
        }).catch(error => {
          console.error('Failed to update lead refine params:', error)
        })
      } catch (error) {
        console.error('An unexpected error occurred while updating lead refine params:', error)
      }
    },

    /**
     * Add a policy to the comparison, or remove it if already selected.
     * Silently no-ops when the cap is reached — the QRP surfaces that to the
     * user via PolicyItem's cap click guard, so this stays a pure state guard.
     *
     * @param {object} policy full policy object from the quotes payload
     * @param {number} max active cap (follows the AB variant)
     * @returns {boolean} true when the selection changed
     */
    toggleComparePolicy (policy, max = MAX_COMPARE_POLICIES) {
      const id = policy?.id

      if (id === undefined || id === null) {
        return false
      }

      if (this.comparePolicies.some(p => `${p?.id}` === `${id}`)) {
        this.removeComparePolicy(id)
        return true
      }

      if (this.comparePolicies.length >= max) {
        return false
      }

      this.comparePolicies = [...this.comparePolicies, policy]
      this.comparePolicyIds = this.comparePolicies.map(p => p?.id)
      return true
    },

    removeComparePolicy (id) {
      this.comparePolicies = this.comparePolicies.filter(p => `${p?.id}` !== `${id}`)
      this.comparePolicyIds = this.comparePolicies.map(p => p?.id)
    },

    clearComparePolicies () {
      this.comparePolicies = []
      this.comparePolicyIds = []
    },

    /**
     * Snapshot the envelope-level context the comparison view needs. Two groups:
     * the rebate fields it renders, and the lead/query fields its Apply Now needs
     * to hand off exactly like the QRP card does. Captured on the QRP because
     * that's where all of it is already resolved.
     */
    setCompareDetails (details) {
      this.compareDetails = {
        rebate: details?.rebate ?? '',
        rebate_percentage: details?.rebate_percentage ?? '',
        income_tier: details?.income_tier ?? '',
        state: details?.state ?? '',
        age_primary: details?.age_primary ?? '',
        age_secondary: details?.age_secondary ?? '',
        source: details?.source ?? '',
        // Stored raw and normalised on read: the store is a dumb snapshot, and
        // the raw value is what you want when debugging a bad lead.
        cover_type: details?.cover_type ?? '',
        standAloneProductDetails: !!details?.standAloneProductDetails
      }
    },

    /**
     * Re-sync comparePolicies from an id list — used by the checkbox path, where
     * comparePolicyIds is written directly by v-model and the full objects have to
     * be looked up afterwards. Also drops ids that are no longer in the result set
     * (e.g. after a refine), keeping the two arrays consistent.
     *
     * @param {Array} ids selected policy ids
     * @param {Array} policies current result set to resolve ids against
     */
    syncComparePolicies (ids, policies) {
      const idList = (ids ?? []).map(id => `${id}`)

      this.comparePolicies = (policies ?? []).filter(p => idList.includes(`${p?.id}`))

      const nextIds = this.comparePolicies.map(p => p?.id)

      // Only reassign when the contents actually changed. comparePolicyIds is the
      // v-model source for the compare checkbox, and MoleculesSelection re-emits
      // `update` on every modelValue change via a deep watcher — so handing back a
      // fresh array with identical contents loops forever:
      //   update -> syncComparePolicies -> new array identity -> update -> ...
      // Vue aborts that at 100 iterations with "Maximum recursive updates exceeded".
      const unchanged = nextIds.length === this.comparePolicyIds.length &&
        nextIds.every((id, index) => `${id}` === `${this.comparePolicyIds[index]}`)

      if (!unchanged) {
        this.comparePolicyIds = nextIds
      }
    }
  },

  persist: {
    storage: piniaPluginPersistedstate.sessionStorage()
  }
})
